import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from 'react-redux';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import {
    Briefcase,
    ArrowUpRight,
    Globe,
    Zap,
    User,
    Bell,
    Calendar,
    LogOut
} from 'lucide-react';

// API & Utility Imports
import { fetchzcql } from '../api/reusableApis';
import {
    transformWeeklyData,
    transformMonthlyData,
    transformYearlyData,
    getCurrentWeekRange,
    getCurrentMonth
} from '.././resuablefunc';

const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EC4899"];


export default function MobileResponsiveDashboard() {
    const [loader, setLoader] = useState(true);
    const currentLoggedInUser = useSelector((state) => state.auth.currentUser);
    const [timeframe, setTimeframe] = useState("weekly");

    // Unified State for Metrics
    const [metrics, setMetrics] = useState({
        total_booking: 0,
        total_booking_today: 0,
        booking_percent_change: 0,
        booking_trend: 'same',
        overall_conversion_rate: 0,
        conversion_percent_change: 0,
        conversion_trend: 'same',
        today_revenue: 0,
        overall_revenue: 0,
        monthly_revenue: 0,
        revenue_percent_change: 0,
        revenue_trend: 'same'
    });

    // Chart Data States
    const [chartData, setChartData] = useState({ weekly: [], monthly: [], yearly: [] });
    const [revenueChartData, setRevenueChartData] = useState({ weekly: [], monthly: [], yearly: [] });

    // ---------- 1. FETCH CHARTS ----------
    const fetchCharts = useCallback(async (type) => {
        try {
            if (!currentLoggedInUser?.users?.ROWID) return;

            let bookingSql = "";
            let revenueSql = "";
            const year = new Date().getFullYear();
            const telecallerId = currentLoggedInUser.users.ROWID;

            if (type === 'weekly') {
                const { startDate, endDate } = getCurrentWeekRange();
                bookingSql = `SELECT created_date, COUNT(ROWID) AS total FROM Bookings WHERE created_date BETWEEN '${startDate}' AND '${endDate}' AND telecaller_id = '${telecallerId}' AND (status = 'BOOKED' or status = 'COMPLETED') GROUP BY created_date ORDER BY created_date`;
                revenueSql = `SELECT created_date, SUM(total_amount) AS total FROM Bookings WHERE created_date BETWEEN '${startDate}' AND '${endDate}'  AND telecaller_id = '${telecallerId}' AND (status = 'BOOKED' or status = 'COMPLETED') GROUP BY created_date ORDER BY created_date`;
            } else if (type === 'monthly') {
                const { year: curYear, month } = getCurrentMonth();
                const daysInMonth = new Date(curYear, month, 0).getDate();
                const start = `${curYear}-${String(month).padStart(2, '0')}-01`;
                const end = `${curYear}-${String(month).padStart(2, '0')}-${daysInMonth}`;
                bookingSql = `SELECT created_date, COUNT(ROWID) AS total FROM Bookings WHERE created_date BETWEEN '${start}' AND '${end}' AND telecaller_id = '${telecallerId}' AND (status = 'BOOKED' or status = 'COMPLETED') GROUP BY created_date ORDER BY created_date`;
                revenueSql = `SELECT created_date, SUM(total_amount) AS total FROM Bookings WHERE created_date BETWEEN '${start}' AND '${end}' AND telecaller_id = '${telecallerId}' AND (status = 'BOOKED' or status = 'COMPLETED') GROUP BY created_date ORDER BY created_date`;
            } else {
                bookingSql = `SELECT created_date, COUNT(ROWID) AS total FROM Bookings WHERE created_date BETWEEN '${year}-01-01' AND '${year}-12-31' AND telecaller_id = '${telecallerId}' AND (status = 'BOOKED' or status = 'COMPLETED') GROUP BY created_date ORDER BY created_date`;
                revenueSql = `SELECT created_date, SUM(total_amount) AS total FROM Bookings WHERE created_date BETWEEN '${year}-01-01' AND '${year}-12-31' AND telecaller_id = '${telecallerId}' AND (status = 'BOOKED' or status = 'COMPLETED') GROUP BY created_date ORDER BY created_date`;
            }

            const [bookRes, revRes] = await Promise.all([
                fetchzcql(bookingSql),
                fetchzcql(revenueSql)
            ]);

            const safeTransform = (res, t) => {
                if (!res?.data?.success || !res?.data?.response) return [];
                const data = res.data.response;
                if (t === 'weekly') return transformWeeklyData(data);
                if (t === 'monthly') return transformMonthlyData(data, new Date().getFullYear(), new Date().getMonth() + 1);
                return transformYearlyData(data);
            };

            setChartData(prev => ({ ...prev, [type]: safeTransform(bookRes, type) }));
            setRevenueChartData(prev => ({ ...prev, [type]: safeTransform(revRes, type) }));
        } catch (err) {
            console.error("Chart Fetch Error:", err);
        }
    }, [currentLoggedInUser]);

    // ---------- 2. FETCH KPI METRICS ----------
    const fetchMetrics = async () => {
        if (!currentLoggedInUser?.users?.ROWID) return;

        try {
            const formatDate = (d) =>
                d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

            const today = formatDate(new Date());
            const yesterdayDate = new Date();
            yesterdayDate.setDate(yesterdayDate.getDate() - 1);
            const yesterday = formatDate(yesterdayDate);
            const telecallerId = currentLoggedInUser.users.ROWID;

            const now = new Date();
            const monthStartStr = formatDate(new Date(now.getFullYear(), now.getMonth(), 1));
            const monthEndStr = formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

            const queries = {
                tBooked: `SELECT COUNT(ROWID) FROM Bookings WHERE created_date = '${today}' AND (status = 'BOOKED' or status = 'COMPLETED') AND telecaller_id = '${telecallerId}'`,
                yBooked: `SELECT COUNT(ROWID) FROM Bookings WHERE created_date = '${yesterday}' AND (status = 'BOOKED' or status = 'COMPLETED') AND telecaller_id = '${telecallerId}'`,
                tAtt: `SELECT COUNT(ROWID) FROM Bookings WHERE created_date = '${today}' AND telecaller_id = '${telecallerId}'`,
                yAtt: `SELECT COUNT(ROWID) FROM Bookings WHERE created_date = '${yesterday}' AND telecaller_id = '${telecallerId}'`,
                totBooked: `SELECT COUNT(ROWID) FROM Bookings WHERE (status = 'BOOKED' or status = 'COMPLETED') AND telecaller_id = '${telecallerId}'`,
                totAtt: `SELECT COUNT(ROWID) FROM Bookings WHERE telecaller_id = '${telecallerId}'`,
                tRev: `SELECT SUM(total_amount) FROM Bookings WHERE created_date = '${today}' AND (status = 'BOOKED' or status = 'COMPLETED') AND telecaller_id = '${telecallerId}'`,
                yRev: `SELECT SUM(total_amount) FROM Bookings WHERE created_date = '${yesterday}' AND (status = 'BOOKED' or status = 'COMPLETED') AND telecaller_id = '${telecallerId}'`,
                totRev: `SELECT SUM(total_amount)  FROM Bookings  WHERE (status = 'BOOKED' or status = 'COMPLETED')   AND telecaller_id = '${telecallerId}'`,
                mRev: `SELECT SUM(total_amount) FROM Bookings WHERE created_date BETWEEN '${monthStartStr}' AND '${monthEndStr}'   AND (status = 'BOOKED' or status = 'COMPLETED')  AND telecaller_id = '${telecallerId}'`
            };


            const results = await Promise.all(Object.values(queries).map(q => fetchzcql(q)));
            const getVal = (res, key) => Number(res?.data?.response?.[0]?.bookings?.[key]) || 0;

            const d = {
                tBooked: getVal(results[0], 'COUNT(ROWID)'),
                yBooked: getVal(results[1], 'COUNT(ROWID)'),
                tAtt: getVal(results[2], 'COUNT(ROWID)'),
                yAtt: getVal(results[3], 'COUNT(ROWID)'),
                totBooked: getVal(results[4], 'COUNT(ROWID)'),
                totAtt: getVal(results[5], 'COUNT(ROWID)'),
                tRev: getVal(results[6], 'SUM(total_amount)'),
                yRev: getVal(results[7], 'SUM(total_amount)'),
                totRev: getVal(results[8], 'SUM(total_amount)'),
                mRev: getVal(results[9], 'SUM(total_amount)')
            };

            const calcTrend = (now, prev) => {
                if (prev === 0) return now > 0 ? { type: 'increase', pc: 100 } : { type: 'same', pc: 0 };
                const diff = ((now - prev) / prev) * 100;
                return { type: diff >= 0 ? 'increase' : 'decrease', pc: Math.abs(Math.round(diff)) };
            };

            const tConv = d.tAtt === 0 ? 0 : (d.tBooked / d.tAtt) * 100;
            const yConv = d.yAtt === 0 ? 0 : (d.yBooked / d.yAtt) * 100;

            setMetrics({
                total_booking: d.totBooked,
                total_booking_today: d.tBooked,
                booking_percent_change: calcTrend(d.tBooked, d.yBooked).pc,
                booking_trend: calcTrend(d.tBooked, d.yBooked).type,
                overall_conversion_rate: d.totAtt === 0 ? 0 : ((d.totBooked / d.totAtt) * 100).toFixed(1),
                conversion_percent_change: calcTrend(tConv, yConv).pc,
                conversion_trend: calcTrend(tConv, yConv).type,
                today_revenue: d.tRev,
                overall_revenue: d.totRev,
                monthly_revenue: d.mRev,
                revenue_percent_change: calcTrend(d.tRev, d.yRev).pc,
                revenue_trend: calcTrend(d.tRev, d.yRev).type
            });
        } catch (err) {
            console.error("Metrics Fetch Error:", err);
        }
    };

    useEffect(() => {
        const init = async () => {
            if (currentLoggedInUser) {
                setLoader(true);
                await Promise.all([fetchMetrics(), fetchCharts(timeframe)]);
                setLoader(false);
            }
        };
        init();
    }, [timeframe, fetchCharts, currentLoggedInUser]);

    if (loader) return (
        <div className="flex items-center justify-center h-screen w-full bg-[#F8FAFC]">
            <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-slate-200 border-t-purple-500 rounded-full animate-spin" />
                <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Loading Dashboard</p>
            </div>
        </div>
    );

    return (
        <div className="h-screen w-full bg-[#F8FAFC] overflow-hidden flex flex-col">

            {/* --- HEADER --- */}
            <header className="w-full bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between shrink-0 z-10 max-sm:pt-[5rem]
">
                <div className="flex flex-col">
                    <h1 className="text-xl font-black text-slate-900 tracking-tight leading-none">
                        Welcome, <span className="text-purple-500">{currentLoggedInUser?.users?.name || "Agent"}</span>
                    </h1>
                    <div className="flex items-center gap-2 mt-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Live Performance Tracker
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-4 sm:gap-6">
                    <div className="hidden sm:flex items-center gap-2 pr-4 border-r border-slate-100">
                        <Calendar size={16} className="text-slate-400" />
                        <span className="text-xs font-bold text-slate-600">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden md:flex flex-col items-end">
                            <span className="text-sm font-black text-slate-800 leading-none">
                                {currentLoggedInUser?.users?.name}
                            </span>
                            <span className="text-[9px] font-black text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md mt-1 uppercase">
                                Telecaller
                            </span>
                        </div>
                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-purple-400 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-purple-100 border-2 border-white transition-transform hover:scale-105 cursor-pointer">
                            <span className="text-sm font-black">
                                {currentLoggedInUser?.users?.name?.charAt(0) || <User size={18} />}
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 w-full overflow-y-auto p-4 sm:p-6 lg:p-10 scroll-smooth">

                {/* KPI CARDS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 mb-8">
                    <StatCard
                        title="Total Bookings"
                        value={metrics.total_booking}
                        sub={`+${metrics.total_booking_today} today`}
                        icon={<Globe size={18} />}
                        variant="purple" // Different color
                    />
                    <StatCard
                        title="Conversion Rate"
                        value={`${metrics.overall_conversion_rate}%`}
                        sub="Total Ratio"
                        icon={<Zap size={18} />}
                        variant="rose" // Different color
                    />
                    <StatCard
                        title="Total Net Revenue"
                        value={`₹${metrics.overall_revenue.toLocaleString()}`}
                        sub="Cumulative"
                        icon={<Briefcase size={18} />}
                        variant="purple" // Different color
                    />
                    <StatCard
                        title="Revenue (Month)"
                        value={`₹${metrics.monthly_revenue.toLocaleString()}`}
                        sub="Current Month"
                        icon={<ArrowUpRight size={18} />}
                        variant="purple"// Different color
                    />
                </div>

                <div className="grid grid-cols-12 gap-6 pb-10">
                    {/* Bookings Chart */}
                    <ChartWrapper title="Booking Activity" timeframe={timeframe} setTimeframe={setTimeframe} color="#10B981">
                        <AreaChart data={chartData[timeframe]} margin={{ left: -20 }} style={{ outline: 'none' }}>
                            <defs>
                                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10 }} dy={10} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10 }} width={40} />
                            <Tooltip content={<CustomTooltip unit="Bookings" color="#10B981" />} wrapperStyle={{ outline: 'none' }} />
                            <Area type="monotone" dataKey="tasks" stroke="#10B981" strokeWidth={3} fill="url(#colorValue)" style={{ outline: 'none' }} />
                        </AreaChart>
                    </ChartWrapper>

                    {/* Donut Chart */}
                    <div className="col-span-12 xl:col-span-4 bg-white border border-slate-100 rounded-[32px] p-8 shadow-sm flex flex-col items-center">
                        <h3 className="w-full text-lg font-bold text-slate-800 mb-6 text-left">Channel Distribution</h3>

                        <div className="h-[250px] w-full flex items-center justify-center">
                            {chartData[timeframe] && chartData[timeframe].length > 0 && chartData[timeframe].reduce((sum, item) => sum + (item.tasks || 0), 0) > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart style={{ outline: 'none' }}>
                                        <Pie
                                            data={chartData[timeframe]}
                                            innerRadius="70%"
                                            outerRadius="90%"
                                            paddingAngle={8}
                                            dataKey="tasks"
                                            stroke="none"
                                        >
                                            {chartData[timeframe].map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip unit="Bookings" color="#10B981" />} wrapperStyle={{ outline: 'none' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex flex-col items-center justify-center text-center">
                                    <div className="w-32 h-32 rounded-full border-4 border-slate-50 flex items-center justify-center mb-4">
                                        <span className="text-slate-200 text-4xl">?</span>
                                    </div>
                                    <p className="text-slate-400 text-sm font-medium">No channel data available <br /> for this period</p>
                                </div>
                            )}
                        </div>

                        <div className="w-full mt-6 max-h-[160px] overflow-y-auto pr-2 custom-scrollbar">
                            <div className="space-y-3">
                                {chartData[timeframe]?.map((entry, index) => (
                                    <LegendRow
                                        key={entry.name}
                                        label={entry.name}
                                        value={entry.tasks}
                                        dotColor={COLORS[index % COLORS.length]}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

function StatCard({ title, value, sub, icon, variant = "purple" }) {
    // Exact color hex codes from the provided image
    const themes = {
        purple: {
            cardBg: "#FFF9EE", // Very light purple/cream
            iconBg: "#F3912E", // Bright purple
            text: "#F3912E",
            icon: <User size={20} />
        },
        rose: {
            cardBg: "#FFF1F9", // Very light pink
            iconBg: "#F75BB1", // Vibrant pink
            text: "#F75BB1",
            icon: <Briefcase size={20} />
        },
        purple: {
            cardBg: "#F5F3FF", // Light lavender
            iconBg: "#635BFF", // Deep purple/blue
            text: "#635BFF",
            icon: <Zap size={20} />
        },
        purple: {
            cardBg: "#F0FDF4", // Light mint
            iconBg: "#4ADE80", // Success green
            text: "#4ADE80",
            icon: <Briefcase size={20} />
        },
    };

    const theme = themes[variant] || themes.purple;

    return (
        <div
            style={{ backgroundColor: theme.cardBg }}
            className="relative overflow-hidden rounded-[20px] p-5 flex flex-col justify-between h-40 shadow-sm border border-white/50"
        >
            {/* Top Row: Title and Circular Icon */}
            <div className="flex justify-between items-start">
                <h3 className="text-sm font-semibold text-slate-700 tracking-tight">
                    {title}
                </h3>
                <div
                    style={{ backgroundColor: theme.iconBg }}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md shadow-black/5"
                >
                    {React.cloneElement(icon, { size: 18, strokeWidth: 2.5 })}
                </div>
            </div>

            {/* Middle Row: Value */}
            <div className="mt-1">
                <span className="text-3xl font-bold text-slate-900 tracking-tight">
                    {value}
                </span>
            </div>

            {/* Bottom Row: Trend Line */}
            <div className="mt-4 flex items-center gap-2">
                <span className="text-xs font-medium text-slate-400">{sub}
                </span>
            </div>
        </div>
    );
}

function ChartWrapper({ title, timeframe, setTimeframe, color, children }) {
    return (
        <div className="col-span-12 xl:col-span-8 bg-white border border-slate-100 rounded-[32px] p-6 lg:p-8 shadow-sm">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <h3 className="text-lg font-bold text-slate-800">{title}</h3>
                <div className="flex bg-slate-100 p-1 rounded-2xl w-full sm:w-auto shadow-inner">
                    {["weekly", "monthly", "yearly"].map((type) => (
                        <button
                            key={type}
                            onClick={() => setTimeframe(type)}
                            className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-xl transition-all capitalize ${timeframe === type ? "bg-white text-purple-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>
            <div className="h-[300px] lg:h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    {children}
                </ResponsiveContainer>
            </div>
        </div>
    );
}

const CustomTooltip = ({ active, payload, unit, color, isCurrency }) => {
    if (active && payload && payload.length) {
        const val = isCurrency ? `₹${payload[0].value.toLocaleString()}` : `${payload[0].value} ${unit}`;
        return (
            <div className="bg-slate-900 text-white p-4 rounded-2xl shadow-2xl border-none">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-1">{payload[0].payload.name}</p>
                <p className="text-lg font-bold" style={{ color }}>{val}</p>
            </div>
        );
    }
    return null;
};

function LegendRow({ label, value, dotColor }) {
    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: dotColor }} />
                <span className="text-sm font-bold text-slate-600 truncate max-w-[120px]">
                    {label}
                </span>
            </div>
            <span className="text-sm font-black text-slate-900">
                {Number(value).toLocaleString()}
            </span>
        </div>
    );
}